import { Link } from "react-router-dom";
import { MessageCircle, Check, ArrowLeft, Camera } from "lucide-react";
import { business, waLink } from "../config/business";
import { LANDING_PAGES } from "../data/landingPages";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatsAppFloat from "../components/WhatsAppFloat";
import Seo from "../components/Seo";
import { Reveal } from "../components/shared";

const WA_TEXT = "Hi! I need a locksmith. My situation: ";

const WaButton = ({ testid, label, className = "" }) => (
  <a data-testid={testid} href={waLink(WA_TEXT)} target="_blank" rel="noopener noreferrer" className={`btn btn-wa rounded-2xl px-7 py-4 text-base sm:text-lg ${className}`}>
    <MessageCircle className="h-5 w-5" strokeWidth={2.5} />
    {label}
  </a>
);

const PageFaq = ({ items }) => (
  <div className="mt-14">
    <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-[-0.02em]">Frequently asked questions</h2>
    <dl className="mt-6 space-y-4">
      {items.map((f) => (
        <div key={f.q} className="card-elevated rounded-2xl p-6">
          <dt className="font-heading text-lg font-semibold">{f.q}</dt>
          <dd className="mt-2 text-slate-400 leading-relaxed">{f.a}</dd>
        </div>
      ))}
    </dl>
  </div>
);

const RelatedPages = ({ current }) => (
  <nav aria-label="Other locksmith pages" className="mt-14">
    <h2 className="font-mono text-xs uppercase tracking-widest text-slate-500">More locksmith services and areas</h2>
    <ul className="mt-4 flex flex-wrap gap-2.5">
      {LANDING_PAGES.filter((p) => p.slug !== current).map((p) => (
        <li key={p.slug}>
          <Link data-testid={`related-link-${p.slug}`} to={`/${p.slug}`} className="inline-block rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-sm text-slate-300 transition-colors hover:border-amber-400/40 hover:text-amber-200">
            {p.h1}
          </Link>
        </li>
      ))}
    </ul>
  </nav>
);

export default function LandingPage({ page }) {
  return (
    <>
      <Seo page={{ title: page.title, description: page.description, path: page.slug, faq: page.faq }} />
      <div className="grain-overlay min-h-screen bg-[var(--bg-main)] font-body text-slate-50">
        <Header hideLanguage />
        <main data-testid="landing-page" className="relative isolate overflow-hidden pt-28 sm:pt-36 pb-32 sm:pb-28">
          <div className="pointer-events-none absolute -top-40 right-[-10%] -z-10 h-[32rem] w-[32rem] rounded-full bg-amber-400/10 blur-3xl" />
          <div className="pointer-events-none absolute bottom-0 left-[-10%] -z-10 h-[28rem] w-[28rem] rounded-full bg-blue-600/15 blur-3xl" />
          <article className="mx-auto max-w-4xl px-5 sm:px-8">
            <Reveal>
              <Link data-testid="landing-home-link" to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-400 transition-colors hover:text-amber-300">
                <ArrowLeft className="h-4 w-4" />
                Back to {business.name} homepage
              </Link>
              <div className="mt-8 mb-5 h-1 w-14 rounded-full bg-gradient-to-r from-yellow-300 via-amber-400 to-amber-600 shadow-[0_0_18px_rgba(234,179,8,0.45)]" />
              <h1 data-testid="landing-h1" className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-[-0.035em]">
                {page.h1}
              </h1>
              <p data-testid="landing-intro" className="mt-7 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-300">{page.intro}</p>
              <div className="mt-9 flex flex-col gap-4 sm:flex-row">
                <WaButton testid="landing-whatsapp-cta" label="Message us on WhatsApp" />
                <a data-testid="landing-photos-cta" href={waLink("Hi! I'm sending photos of my door, lock or vehicle. My location is: ")} target="_blank" rel="noopener noreferrer" className="btn btn-sky rounded-2xl px-7 py-4 text-base sm:text-lg">
                  <Camera className="h-5 w-5" strokeWidth={2.5} />
                  Send photos on WhatsApp
                </a>
              </div>
              <p className="mt-5 inline-flex items-center gap-2.5 rounded-2xl border border-amber-400/25 bg-amber-400/10 px-4 py-3 text-sm sm:text-base font-semibold text-amber-200">
                <Camera className="h-4 w-4 shrink-0 text-amber-400" strokeWidth={2.5} />
                Send a photo, get a clear written price before any work starts.
              </p>
            </Reveal>

            <Reveal delay={0.1}>
              <ul data-testid="landing-bullets" className="mt-12 grid gap-3 sm:grid-cols-2">
                {page.bullets.map((b) => (
                  <li key={b} className="card-elevated flex items-start gap-3 rounded-2xl px-5 py-4 text-slate-200">
                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" strokeWidth={2.5} />
                    {b}
                  </li>
                ))}
              </ul>
            </Reveal>

            {page.sections.map((s, i) => (
              <Reveal key={s.h2} delay={0.1 + i * 0.05}>
                <section className="mt-12">
                  <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-[-0.02em]">{s.h2}</h2>
                  <p className="mt-4 text-base sm:text-lg leading-relaxed text-slate-400">{s.p}</p>
                </section>
              </Reveal>
            ))}

            <Reveal delay={0.15}>
              <PageFaq items={page.faq} />
              <div className="card-elevated mt-14 flex flex-col items-start gap-5 rounded-3xl p-7 sm:p-9 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="font-heading text-xl sm:text-2xl font-bold tracking-[-0.02em]">Ready when you are, 24/7.</p>
                  <p className="mt-2 text-slate-400">WhatsApp: {business.phoneDisplay} · {business.email}</p>
                </div>
                <WaButton testid="landing-whatsapp-cta-bottom" label="Message us on WhatsApp" className="w-full shrink-0 sm:w-auto" />
              </div>
              <RelatedPages current={page.slug} />
            </Reveal>
          </article>
        </main>
        <Footer />
        <WhatsAppFloat />
      </div>
    </>
  );
}
